import { router, usePage } from "@inertiajs/vue3";
import Intercom, { boot, shutdown, update } from "@intercom/messenger-js-sdk";

import { browserCookieChoice, forgetBrowserCookieChoice } from "./cookieConsent";
import { announceToOtherTabs, onSignalFromOtherTab } from "./crossTabSignal";
import { logError } from "./logUtils";

/** Mirrors `IntercomMessengerSettings` built server-side in accounts/services/intercom_service.py. */
export interface IntercomMessengerSettings {
    app_id: string;
    user_id: string;
    user_hash: string;
    email: string;
    name?: string;
    created_at?: number;
}

let bootedUserId: string | null = null;
/** The SDK's default export installs the widget script and boots once; after that only `boot()` restarts it. */
let sdkInstalled = false;

/**
 * The last settings a server response carried (`null` = server said off). `undefined` until the
 * first page: before that, the page's own props are the freshest thing we have. Inertia restores
 * earlier pages from history with their old props and no server round-trip, so a boot decision
 * reads this instead of the restored page.
 */
let serverSettings: IntercomMessengerSettings | null | undefined;

/** Bumped by every shutdown; a positive server response from a visit started before it is ignored. */
let shutdownEpoch = 0;

/** Inertia's visit object, stamped on `start` / `onSuccess` and read back on `finish` (same object). */
export interface IntercomVisitStamp {
    intercomEpoch?: number;
    intercomSeq?: number;
    intercomResponse?: { settings: IntercomMessengerSettings | null | undefined };
}

/**
 * A choice made on the login page waits in localStorage until the banner transfers it to the
 * account (see CookieBanner.vue). Until then the account's older choice must not win.
 */
const browserOptOutPending = (accountUuid: string): boolean => {
    const latestBrowserChoice = browserCookieChoice(accountUuid);
    if (latestBrowserChoice !== null && latestBrowserChoice !== "all") {
        return true;
    }
    try {
        const pending = localStorage.getItem("cookie_consent");
        if (!pending || pending === "persisted") {
            return false;
        }
        return new URLSearchParams(pending).get("consent_choice") !== "all";
    } catch {
        return false;
    }
};

const INTERCOM_COOKIE_PREFIX = "intercom-";
const INTERCOM_SHUTDOWN_KEY = "intercom_shutdown_at";
const INTERCOM_SIGNED_OUT_KEY = "intercom_signed_out_at";
const INTERCOM_ACCOUNT_KEY = "intercom_account";
/**
 * Set when this tab, or another tab of this browser, logged out or lost its session. The server
 * can still answer a visit started in the gap before the logout request lands, so no answer may
 * boot until a full page load (a login) resets the module.
 */
let signedOut = false;

/**
 * Intercom's `shutdown()` leaves some of its cookies behind (notably `intercom-id-*`); its docs
 * say to expire every `intercom-` prefixed cookie on our domain instead. They are non-essential
 * cookies, so they must go whenever the user is no longer consenting (or no longer booted).
 */
export const clearIntercomCookies = (): void => {
    const hostname = globalThis.location?.hostname ?? "";
    const domains = hostname ? [hostname, `.${hostname.split(".").slice(-2).join(".")}`] : [];
    const expired = "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    for (const cookie of document.cookie.split(";")) {
        const name = cookie.split("=")[0].trim();
        if (!name.startsWith(INTERCOM_COOKIE_PREFIX)) {
            continue;
        }
        document.cookie = `${name}${expired}`;
        for (const domain of domains) {
            document.cookie = `${name}${expired}; domain=${domain}`;
        }
    }
};

const turnOff = (fenceInFlightVisits: boolean, tellOtherTabs = true): void => {
    serverSettings = null;
    if (fenceInFlightVisits) {
        shutdownEpoch += 1;
    }
    if (bootedUserId !== null) {
        shutdown();
        bootedUserId = null;
        // Only a tab that really had a messenger tells the others, so the notice cannot bounce
        // back and forth between tabs that have already shut down.
        if (tellOtherTabs) {
            announceToOtherTabs(INTERCOM_SHUTDOWN_KEY);
        }
    }
    clearIntercomCookies();
};

/** Also fences out every visit still in flight: their answers predate whatever caused this. */
export const shutdownIntercomMessenger = (): void => turnOff(true);

/**
 * For logout and session expiry: shut down here and tell every other tab regardless of whether
 * this one had a messenger (it may not have, e.g. after masquerading), since an idle tab may
 * still show the account's launcher. Every tab then stays off until its next full page load.
 */
export const shutdownIntercomMessengerEverywhere = (): void => {
    signedOut = true;
    forgetBrowserCookieChoice();
    shutdownIntercomMessenger();
    announceToOtherTabs(INTERCOM_SIGNED_OUT_KEY);
};

const onSignedOutElsewhere = (): void => {
    signedOut = true;
    shutdownIntercomMessenger();
};

let announcedAccount: string | null | undefined;

/**
 * Tells the other tabs whose account this tab's pages now belong to. A sign-in replaces the
 * browser's session without any logout, so a tab idling on the previous account's messenger
 * would otherwise keep showing it until it next navigates.
 */
export const announceIntercomAccount = (userUuid: string | null): void => {
    if (userUuid === announcedAccount) {
        return;
    }
    announcedAccount = userUuid;
    announceToOtherTabs(INTERCOM_ACCOUNT_KEY, userUuid ?? "");
};

/**
 * Another tab signed into a different account: this tab's page belongs to the previous one, so
 * nothing may boot here until a full page load shows whose page it is (a partial reload would
 * put the new account's messenger on the old account's page). The generic shutdown notice is
 * not sent: the tab that signed in has just booted, and must not be closed by this.
 */
const onAccountElsewhere = (notice: string | null): void => {
    const userUuid = notice?.split("@")[0] ?? "";
    const thisTabsAccount = announcedAccount === undefined ? bootedUserId : announcedAccount;
    if (thisTabsAccount !== null && thisTabsAccount !== userUuid) {
        signedOut = true;
        turnOff(true, false);
    }
};

/**
 * A logout, session expiry, consent withdrawal or sign-in to another account in one tab leaves the
 * messenger mounted in every other open tab until it next navigates; this closes it there right away.
 */
export const shutdownIntercomMessengerWithOtherTabs = (): void => {
    onSignalFromOtherTab(INTERCOM_SHUTDOWN_KEY, shutdownIntercomMessenger);
    onSignalFromOtherTab(INTERCOM_SIGNED_OUT_KEY, onSignedOutElsewhere);
    onSignalFromOtherTab(INTERCOM_ACCOUNT_KEY, onAccountElsewhere);
};

export const initializeIntercomMessenger = (settings: IntercomMessengerSettings | null | undefined): void => {
    if (!settings || signedOut || browserOptOutPending(settings.user_id)) {
        shutdownIntercomMessenger();
        return;
    }
    try {
        if (bootedUserId === settings.user_id) {
            serverSettings = settings;
            // Tells the messenger a SPA navigation happened so it can refresh unread counts,
            // and carries any profile or hash change the server made since boot.
            update(settings);
            return;
        }
        shutdownIntercomMessenger();
        serverSettings = settings;
        const bootSettings = { ...settings, hide_default_launcher: false };
        if (sdkInstalled) {
            boot(bootSettings);
        } else {
            Intercom(bootSettings);
            sdkInstalled = true;
        }
        bootedUserId = settings.user_id;
    } catch (error) {
        logError(error, { context: { source: "intercom" } });
    }
};

/**
 * For Inertia's `navigate` event, which fires for the first page, every server visit (just before
 * `success`) and history restores. Only the first page may boot from the page's own props; a
 * server visit is handled by `applyIntercomServerResponse`, so a booted messenger is left alone
 * here (one `update` per visit); a history restore re-applies what the server last allowed.
 */
/**
 * Whether this browser signed out after this page was requested: the page's settings are then
 * stale and the one-shot sign-out signal was missed (the script was not running yet). A sign-out
 * older than this page's navigation means a later login, which boots normally.
 */
const signedOutAfterThisPageWasRequested = (): boolean => {
    try {
        const signedOutAt = Number(localStorage.getItem(INTERCOM_SIGNED_OUT_KEY));
        return signedOutAt > 0 && signedOutAt > performance.timeOrigin;
    } catch {
        return false;
    }
};

/**
 * Whether another tab signed into a different account after this page was requested: the page
 * (and its settings) then belong to the previous account, and announcing it would close the new
 * account's messenger elsewhere.
 */
const accountChangedAfterThisPageWasRequested = (pageAccount: string | null): boolean => {
    try {
        const [account, at] = (localStorage.getItem(INTERCOM_ACCOUNT_KEY) ?? "").split("@");
        return Number(at) > performance.timeOrigin && account !== (pageAccount ?? "");
    } catch {
        return false;
    }
};

/**
 * `pageAccount` is the signed-in user's uuid from the page's own props; it is announced to the
 * other tabs only for the first page, never from a restored history entry.
 */
export const syncIntercomMessengerFromHistory = (pageSettings: IntercomMessengerSettings | null | undefined, pageAccount: string | null = null): void => {
    if (serverSettings === undefined) {
        if (accountChangedAfterThisPageWasRequested(pageAccount)) {
            // This page belongs to the previous account; only a full page load can show whose it is.
            signedOut = true;
            serverSettings = null;
            clearIntercomCookies();
            return;
        }
        if (pageSettings && signedOutAfterThisPageWasRequested()) {
            serverSettings = null;
            clearIntercomCookies();
            router.reload({ only: ["intercom_settings"] }); // let the server decide afresh
            return;
        }
        announceIntercomAccount(pageAccount);
        initializeIntercomMessenger(pageSettings);
        return;
    }
    const alreadyOff = serverSettings === null && bootedUserId === null;
    const alreadyOn = serverSettings !== null && bootedUserId === serverSettings.user_id;
    if (alreadyOff || alreadyOn) {
        return;
    }
    initializeIntercomMessenger(serverSettings);
};

/** Call from Inertia's `start` event with the visit, so its response can be matched against shutdowns since. */
let visitSeq = 0;
/** Newest visit whose answer was applied; an older visit's denial is superseded by it. */
let lastAnsweredSeq = 0;

export const markIntercomVisitStarted = (visit: IntercomVisitStamp): void => {
    visit.intercomEpoch = shutdownEpoch;
    visit.intercomSeq = ++visitSeq;
};

/** Signed-out pages (login, magic-link landing) never carry Messenger settings: nothing to refetch. */
const pageHasSignedInUser = (): boolean => {
    const userDetails = usePage().props.user_details as { is_anonymous?: boolean } | undefined;
    return userDetails?.is_anonymous === false;
};

/**
 * After a cookie consent change, in the tab that saved it or one that heard about it. Closes the
 * messenger first, so a failed refetch can't leave it running on revoked consent; the refreshed
 * prop boots it again only if the server now allows it.
 */
export const refreshIntercomMessengerForConsentChange = (): void => {
    shutdownIntercomMessenger();
    if (!pageHasSignedInUser()) {
        return;
    }
    router.reload({ only: ["intercom_settings"] });
};

/** Call from the visit's own `onSuccess`, so the answer is tied to the visit that fetched it. */
export const recordIntercomServerResponse = (visit: IntercomVisitStamp, settings: IntercomMessengerSettings | null | undefined): void => {
    visit.intercomResponse = { settings };
};

/**
 * Call when a visit has finished (Inertia `finish` with `visit.completed`, which is also set for
 * failed responses). Only this visit's own successful answer is used: a failed refetch applies
 * nothing, so a Messenger shut down before it stays off. Visits can overlap, so the fence is per
 * visit: an answer older than one already applied is superseded; a positive answer additionally
 * needs no shutdown since its visit started.
 */
export const applyIntercomServerResponse = (visit: IntercomVisitStamp): void => {
    if (visit.intercomResponse === undefined) {
        return;
    }
    const { settings } = visit.intercomResponse;
    const seq = visit.intercomSeq ?? 0;
    if (!settings) {
        if (seq < lastAnsweredSeq) {
            return; // a newer visit already answered (consent was granted again meanwhile)
        }
        lastAnsweredSeq = seq;
        if (seq < visitSeq) {
            // A newer visit is still in flight and will have the last word; turn off now in case it
            // fails, but without fencing its answer out. A withdrawal shuts down synchronously and
            // its own refetch is the newest visit, so this never delays it.
            turnOff(false);
            return;
        }
        initializeIntercomMessenger(null);
        return;
    }
    if (visit.intercomEpoch !== shutdownEpoch || seq < lastAnsweredSeq) {
        return; // a shutdown happened since, or a newer visit (possibly a denial) already answered
    }
    lastAnsweredSeq = seq;
    initializeIntercomMessenger(settings);
};
